import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useDynamicRefresh, extractMostRecentTimestamp } from '../hooks/useDynamicRefresh'
import { apiClient } from '../api/client'
import { CHUNKED_UPLOAD_THRESHOLD, uploadDocumentChunked } from '../api/chunked-upload'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'
import { Badge } from './ui/badge'
import { ScrollArea } from './ui/scroll-area'
import {
  Alert,
  AlertDescription,
} from './ui/alert'
import { AppIcon } from './ui/icon'
import {
  mdiFileUpload,
  mdiCloudUpload,
  mdiCheckCircle,
  mdiAlertCircle,
  mdiClockOutline,
  mdiLoading,
  mdiCancel,
  mdiAlert,
  mdiFileTable,
  mdiInformation,
  mdiStop,
  mdiDownload,
  mdiHistory,
  mdiPlay,
  mdiRefresh,
  mdiAutorenew,
  mdiReplay,
} from '@mdi/js'
import { useCancelJob, useJobs, fetchJob } from '../api/jobs'
import { useQuery } from '@tanstack/react-query'
import { LatestExecutionLogs } from './LatestExecutionLogs'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'

const statusConfig = {
  pending: { color: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400', icon: mdiClockOutline },
  running: { color: 'bg-primary/10 text-primary', icon: mdiLoading },
  success: { color: 'bg-green-500/10 text-green-600 dark:text-green-400', icon: mdiCheckCircle },
  failed: { color: 'bg-destructive/10 text-destructive', icon: mdiAlertCircle },
  cancelled: { color: 'bg-muted text-muted-foreground', icon: mdiCancel },
  lost: { color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400', icon: mdiAlert },
  manual: { color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400', icon: mdiAlert },
}

interface UploadFile {
  file: File
  id: string
  status: 'pending' | 'uploading' | 'success' | 'failed'
  progress: number
  error?: string
}

interface ImportOptions {
  apply: boolean
  batchSize: number
  global: boolean
  autoCreateSources: boolean
  autoCreateProperties: boolean
  autoCreateAssetTypes: boolean
}


function ValidationResults({ output }: { output: unknown }) {
  if (!output || typeof output !== 'object' || !('validation_summary' in output)) return null

  const outputObj = output as any
  const { validation_summary, validation_errors = [], validation_warnings = [] } = outputObj

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AppIcon path={mdiFileTable} size="md" />
          Validation Results
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {validation_summary.valid_rows || 0}
            </div>
            <div className="text-sm text-muted-foreground">Valid Rows</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-destructive">
              {validation_summary.invalid_rows || 0}
            </div>
            <div className="text-sm text-muted-foreground">Invalid Rows</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-destructive">
              {validation_summary.total_errors || 0}
            </div>
            <div className="text-sm text-muted-foreground">Total Errors</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {validation_summary.total_warnings || 0}
            </div>
            <div className="text-sm text-muted-foreground">Total Warnings</div>
          </div>
        </div>

        {/* Errors */}
        {validation_errors.length > 0 && (
          <div>
            <h4 className="font-medium text-destructive mb-2">Validation Errors</h4>
            <ScrollArea className="h-32">
              <div className="space-y-1">
                {validation_errors.map((error: { row: number; field: string; message: string }, index: number) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 p-2 rounded-md bg-destructive/5 border border-destructive/20 text-sm"
                  >
                    <Badge variant="destructive" className="text-xs shrink-0">
                      Row {error.row}
                    </Badge>
                    <div className="flex-1">
                      <div className="font-medium">{error.field}</div>
                      <div className="text-muted-foreground">{error.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Warnings */}
        {validation_warnings.length > 0 && (
          <div>
            <h4 className="font-medium text-yellow-600 dark:text-yellow-400 mb-2">Validation Warnings</h4>
            <ScrollArea className="h-24">
              <div className="space-y-1">
                {validation_warnings.map((warning: { row: number; field: string; message: string }, index: number) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 p-2 rounded-md bg-yellow-500/5 border border-yellow-500/20 text-sm"
                  >
                    <Badge variant="outline" className="text-xs shrink-0 text-yellow-600 dark:text-yellow-400">
                      Row {warning.row}
                    </Badge>
                    <div className="flex-1">
                      <div className="font-medium">{warning.field}</div>
                      <div className="text-muted-foreground">{warning.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ImportResults({ output }: { output: unknown }) {
  if (!output || typeof output !== 'object' || !('import_summary' in output)) return null

  const outputObj = output as any
  const { import_summary, import_errors = [] } = outputObj

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AppIcon path={mdiCheckCircle} size="md" />
          Import Results
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Import Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {import_summary.assets_created || 0}
            </div>
            <div className="text-sm text-muted-foreground">Assets Created</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-destructive">
              {import_summary.assets_failed || 0}
            </div>
            <div className="text-sm text-muted-foreground">Assets Failed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">
              {import_summary.properties_created || 0}
            </div>
            <div className="text-sm text-muted-foreground">Properties Created</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">
              {import_summary.external_ids_created || 0}
            </div>
            <div className="text-sm text-muted-foreground">External IDs Created</div>
          </div>
        </div>

        {/* Import Errors */}
        {import_errors.length > 0 && (
          <div>
            <h4 className="font-medium text-destructive mb-2">Import Errors</h4>
            <ScrollArea className="h-32">
              <div className="space-y-1">
                {import_errors.map((error: { row: number; asset_identifier?: string; error: string }, index: number) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 p-2 rounded-md bg-destructive/5 border border-destructive/20 text-sm"
                  >
                    <Badge variant="destructive" className="text-xs shrink-0">
                      Row {error.row}
                    </Badge>
                    <div className="flex-1">
                      <div className="font-medium">{error.asset_identifier || 'Unknown Asset'}</div>
                      <div className="text-muted-foreground">{error.error}</div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function DataImportResults({ output }: { output: unknown }) {
  if (!output || typeof output !== 'object' || !('files' in output)) return null

  const outputObj = output as any
  const { files = [], summary } = outputObj

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AppIcon path={mdiCheckCircle} size="md" />
          Import Results
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {summary.total_files || 0}
              </div>
              <div className="text-sm text-muted-foreground">Total Files</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {summary.total_created || 0}
              </div>
              <div className="text-sm text-muted-foreground">Created</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {summary.total_updated || 0}
              </div>
              <div className="text-sm text-muted-foreground">Updated</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-destructive">
                {summary.total_errors || 0}
              </div>
              <div className="text-sm text-muted-foreground">Errors</div>
            </div>
          </div>
        )}

        {/* Per-file results */}
        {files.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium">Files Processed</h4>
            {files.map((file: any, index: number) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 border border-border rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <AppIcon path={mdiFileTable} size="sm" />
                  <div>
                    <div className="font-medium text-sm">{file.filename}</div>
                    <div className="text-xs text-muted-foreground">
                      {file.entity_type} — {file.status}
                    </div>
                  </div>
                </div>
                {file.results?.summary && (
                  <div className="flex gap-3 text-xs text-muted-foreground">
                    {file.results.summary.created > 0 && (
                      <span className="text-green-600 dark:text-green-400">+{file.results.summary.created}</span>
                    )}
                    {file.results.summary.updated > 0 && (
                      <span className="text-primary">~{file.results.summary.updated}</span>
                    )}
                    {file.results.summary.errors > 0 && (
                      <span className="text-destructive">{file.results.summary.errors} errors</span>
                    )}
                  </div>
                )}
                {file.error && (
                  <span className="text-xs text-destructive">{file.error}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function RecentImportJobs({ org }: { org: string }) {
  const { t } = useTranslation('assets')
  const { data: jobsData, isLoading } = useJobs(org, { type: 'data_import', limit: 3 })

  const getJobStatus = (job: any) => {
    if (job.final_status) {
      return job.final_status
    }

    if (job.executions && job.executions.length > 0) {
      const latestExecution = job.executions[0]
      return latestExecution.status
    }

    return 'pending'
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AppIcon path={mdiHistory} size="md" />
            {t('import.recent_jobs_title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <AppIcon path={mdiLoading} size="md" className="motion-safe:animate-spin mr-2" />
            {t('import.recent_jobs_loading')}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!jobsData?.jobs || jobsData.jobs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AppIcon path={mdiHistory} size="md" />
            {t('import.recent_jobs_title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState variant="inline" className="py-4" title={t('import.recent_jobs_empty')} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AppIcon path={mdiHistory} size="md" />
          {t('import.recent_jobs_title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {jobsData.jobs.map((job) => {
          const status = getJobStatus(job)
          const statusInfo = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending

          return (
            <Link
              key={job.uid}
              to="/$org/data/import"
              params={{ org }}
              search={{ jobUid: job.uid }}
              className="block"
            >
              <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <Badge className={statusInfo.color}>
                    <AppIcon path={statusInfo.icon} size="xs" className="mr-1" />
                    {status}
                  </Badge>
                  <div>
                    <div className="font-medium text-sm">
                      {job.properties?.apply ? 'Apply Mode' : 'Dry Run'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(job.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {job.uid.substring(0, 8)}...
                </div>
              </div>
            </Link>
          )
        })}
      </CardContent>
    </Card>
  )
}

export function AssetsImportPage({ jobUid }: { jobUid?: string }) {
  const { t } = useTranslation('assets')
  const { org } = useParams({ strict: false }) as { org: string }
  const navigate = useNavigate()

  // File upload state
  const [uploadFile, setUploadFile] = useState<UploadFile | null>(null)
  const [importOptions, setImportOptions] = useState<ImportOptions>({
    apply: false, // Default to dry run
    batchSize: 100,
    global: false,
    autoCreateSources: false,
    autoCreateProperties: false,
    autoCreateAssetTypes: false,
  })
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Dynamic refresh hook for monitoring changes
  const dynamicRefresh = useDynamicRefresh({
    enabled: !!jobUid,
    defaultInterval: 1000, // Start with 1 second
  })

  // Job tracking with dynamic refresh integration
  const { data: job, isLoading: isJobLoading, error: jobError, refetch } = useQuery({
    queryKey: ['job', org, jobUid],
    queryFn: () => fetchJob(org, jobUid!),
    enabled: !!jobUid,
    refetchInterval: false, // We'll handle refresh manually via dynamic refresh
  })
  const cancelJobMutation = useCancelJob(org)
  const [isCreatingJob, setIsCreatingJob] = useState(false)

  // Handle dynamic refresh - trigger refetch when countdown reaches zero
  useEffect(() => {
    if (!dynamicRefresh.isEnabled || !jobUid) return

    if (dynamicRefresh.timeRemaining === 0) {
      refetch().then(({ data: freshJob }) => {
        if (freshJob) {
          // Extract most recent timestamp from job and executions
          const mostRecentTimestamp = extractMostRecentTimestamp(
            freshJob,
            freshJob.executions || []
          )

          // Check if data has changed since last refresh
          const hasChanged = mostRecentTimestamp !== dynamicRefresh.lastTimestamp

          // Update dynamic refresh with change detection and new timestamp
          dynamicRefresh.controls.adjustRate(hasChanged, mostRecentTimestamp || undefined)

          // Check if job has reached final status and disable auto-refresh
          const jobStatus = getJobStatus(freshJob)
          if (jobStatus === 'success' || jobStatus === 'failed' || jobStatus === 'cancelled') {
            console.log(`Job ${jobUid} completed with status: ${jobStatus}. Stopping auto-refresh.`)
            dynamicRefresh.controls.disable()
          }
        }
      }).catch(error => {
        console.error('Dynamic refresh failed:', error)
        // On error, slow down refresh rate and potentially disable if critical
        dynamicRefresh.controls.adjustRate(false)

        // If this is a 404 or unauthorized error, disable refresh completely
        if (error?.message?.includes('404') || error?.message?.includes('401')) {
          console.warn('Job not found or unauthorized. Disabling auto-refresh.')
          dynamicRefresh.controls.disable()
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicRefresh.timeRemaining, dynamicRefresh.isEnabled, jobUid, refetch, dynamicRefresh.lastTimestamp])

  // Pause dynamic refresh during user interactions and check initial job status
  useEffect(() => {
    if (isUploading) {
      dynamicRefresh.controls.disable()
    } else if (jobUid && job) {
      // Check if job is already in final status
      const jobStatus = getJobStatus(job)
      if (jobStatus === 'success' || jobStatus === 'failed' || jobStatus === 'cancelled') {
        console.log(`Job ${jobUid} already completed with status: ${jobStatus}. Auto-refresh disabled.`)
        dynamicRefresh.controls.disable()
      } else {
        dynamicRefresh.controls.enable()
      }
    } else if (jobUid) {
      // Job is loading, enable refresh to start monitoring
      dynamicRefresh.controls.enable()
    }
  }, [isUploading, jobUid, job, dynamicRefresh])

  // Handle manual refresh button - reset dynamic refresh timer
  const handleManualRefresh = useCallback(() => {
    refetch()
    dynamicRefresh.controls.forceRefresh()
  }, [refetch, dynamicRefresh])

  // Cleanup dynamic refresh on component unmount or when jobUid changes
  useEffect(() => {
    return () => {
      dynamicRefresh.controls.disable()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobUid])

  // Set up breadcrumbs
  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('common:nav.data'), path: `/${org}/data` },
    { title: t('import.title'), path: `/${org}/data/import` }
  ])

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return

    const file = files[0]

    // Validate file type
    const lower = file.name.toLowerCase()
    if (!lower.endsWith('.csv') && !lower.endsWith('.json') && !lower.endsWith('.zip')) {
      setUploadError('Supported formats: CSV, JSON, ZIP')
      return
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024 // 50MB in bytes
    if (file.size > maxSize) {
      setUploadError('File size must be less than 50MB')
      return
    }

    setUploadError(null)
    setUploadFile({
      file,
      id: crypto.randomUUID(),
      status: 'pending',
      progress: 0,
    })
  }, [])

  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event.target.files)
  }, [handleFileSelect])

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    handleFileSelect(event.dataTransfer.files)
  }, [handleFileSelect])

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
  }, [])

  const clearFile = useCallback(() => {
    setUploadFile(null)
    setUploadError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const startImport = useCallback(async () => {
    if (!uploadFile) return

    setIsUploading(true)
    setUploadError(null)

    try {
      // Update file status
      setUploadFile(prev => prev ? { ...prev, status: 'uploading', progress: 0 } : null)

      // Step 1: Upload CSV file as a document
      const uploadFormData = new FormData()
      uploadFormData.append('file', uploadFile.file)
      uploadFormData.append('name', uploadFile.file.name)
      uploadFormData.append('target_entity_type', 'job')
      // A multi-entity CSV ZIP is the payload of THIS import, not a client
      // deposit: it must not be exploded into one document per CSV by
      // deposit_extract (spec 2026-08-16-06 §1).
      uploadFormData.append('extract_archive', 'false')

      let documentUid: string | undefined
      if (uploadFile.file.size > CHUNKED_UPLOAD_THRESHOLD) {
        // A big import file goes up in chunks (spec 2026-10-02-03).
        const uploaded = await uploadDocumentChunked(org, uploadFile.file, {
          name: uploadFile.file.name,
          extract_archive: false,
        })
        documentUid = uploaded.document_uid
      } else {
        const uploadResponse = await apiClient.POST('/api/v1/organizations/{orgCode}/documents', {
          params: { path: { orgCode: org } },
          body: uploadFormData as any,
        })

        if (uploadResponse.error) {
          throw new Error('Failed to upload CSV file')
        }

        documentUid = uploadResponse.data?.uid
      }
      if (!documentUid) {
        throw new Error('No document UID returned from upload')
      }

      // Update file status - upload complete, now creating job
      setUploadFile(prev => prev ? { ...prev, status: 'uploading', progress: 75 } : null)

      // Step 2: Trigger data import via /data/import endpoint
      const importBody = {
        document_uids: [documentUid],
        apply: importOptions.apply,
        options: {
          global: importOptions.global,
          auto_create_sources: importOptions.autoCreateSources,
          auto_create_properties: importOptions.autoCreateProperties,
          auto_create_asset_types: importOptions.autoCreateAssetTypes,
        },
      }

      const importResponse = await (apiClient.POST as any)(`/api/v1/organizations/${org}/data/import`, {
        body: importBody,
      })

      if (importResponse.error) {
        throw new Error('Failed to create import job')
      }

      // Update file status to success
      setUploadFile(prev => prev ? { ...prev, status: 'success', progress: 100 } : null)

      // Navigate to tracking view
      const jobUidResult = importResponse.data?.job_uid
      if (jobUidResult) {
        navigate({
          to: '/$org/data/import',
          params: { org },
          search: { jobUid: jobUidResult }
        })
      }
    } catch (error: unknown) {
      console.error('Import error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to start import'
      setUploadError(errorMessage)
      setUploadFile(prev => prev ? { ...prev, status: 'failed', error: errorMessage } : null)
    } finally {
      setIsUploading(false)
    }
  }, [uploadFile, importOptions, org, navigate])

  const handleCancelJob = useCallback(async () => {
    if (!job?.uid) return

    try {
      await cancelJobMutation.mutateAsync(job.uid)
    } catch (error) {
      console.error('Failed to cancel job:', error)
    }
  }, [job?.uid, cancelJobMutation])

  const handleApplyImport = useCallback(async () => {
    if (!job?.properties?.document_uids) return

    setIsCreatingJob(true)
    try {
      const importBody = {
        document_uids: job.properties.document_uids,
        apply: true,
        options: {
          global: job.properties?.global || false,
          auto_create_sources: job.properties?.auto_create_sources || false,
          auto_create_properties: job.properties?.auto_create_properties || false,
          auto_create_asset_types: job.properties?.auto_create_asset_types || false,
        },
      }

      const importResponse = await (apiClient.POST as any)(`/api/v1/organizations/${org}/data/import`, {
        body: importBody,
      })

      if (importResponse.error) {
        throw new Error('Failed to create apply job')
      }

      const jobUidResult = importResponse.data?.job_uid
      if (jobUidResult) {
        navigate({
          to: '/$org/data/import',
          params: { org },
          search: { jobUid: jobUidResult }
        })
      }
    } catch (error) {
      console.error('Failed to create apply job:', error)
    } finally {
      setIsCreatingJob(false)
    }
  }, [job?.properties, navigate, org])

  const handleRunAgain = useCallback(async () => {
    if (!job?.properties?.document_uids) return

    setIsCreatingJob(true)
    try {
      const importBody = {
        document_uids: job.properties.document_uids,
        apply: job.properties.apply || false,
        options: {
          global: job.properties?.global || false,
          auto_create_sources: job.properties?.auto_create_sources || false,
          auto_create_properties: job.properties?.auto_create_properties || false,
          auto_create_asset_types: job.properties?.auto_create_asset_types || false,
        },
      }

      const importResponse = await (apiClient.POST as any)(`/api/v1/organizations/${org}/data/import`, {
        body: importBody,
      })

      if (importResponse.error) {
        throw new Error('Failed to create run again job')
      }

      const jobUidResult = importResponse.data?.job_uid
      if (jobUidResult) {
        navigate({
          to: '/$org/data/import',
          params: { org },
          search: { jobUid: jobUidResult }
        })
      }
    } catch (error) {
      console.error('Failed to create run again job:', error)
    } finally {
      setIsCreatingJob(false)
    }
  }, [job?.properties, navigate, org])

  const downloadSampleCSV = useCallback(() => {
    const headers = [
      'asset:type',
      'asset:name',
      'asset:start_date',
      'asset:end_date',
      'asset:ext:code',
      'prop:rnb:value',
      'asset:parent:ext:code'
    ]

    const sampleData = [
      [
        'building_group',
        'Campus Alpha',
        '2024-01-01',
        '2030-12-31',
        'CAMPUS-ALPHA-001',
        '123456789',
        ''
      ],
      [
        'building',
        'Building A - Main Office',
        '2024-01-01',
        '2030-12-31',
        'BLDG-A-001',
        '987654321',
        'CAMPUS-ALPHA-001'
      ],
      [
        'unit',
        'Office 101',
        '2024-01-01',
        '2030-12-31',
        'UNIT-101-001',
        '456789123',
        'BLDG-A-001'
      ]
    ]

    const csvContent = [
      headers.join(','),
      ...sampleData.map(row =>
        row.map(cell => {
          if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
            return '"' + cell.replace(/"/g, '""') + '"'
          }
          return cell
        }).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', 'asset-import-template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [])

  // If we're loading a job, show loading state
  if (jobUid && isJobLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={SECTION_ICONS.imports}
          title="Data Import Progress"
          description={`Loading import job: ${jobUid}`}
        />
        <div className="text-center text-muted-foreground py-8">
          <AppIcon path={mdiLoading} size="2xl" className="mx-auto mb-4 motion-safe:animate-spin" />
          Loading job details...
        </div>
      </div>
    )
  }

  // If we have a job error, show error state
  if (jobUid && jobError) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={SECTION_ICONS.imports}
          title="Data Import Progress"
          description={`Error loading import job: ${jobUid}`}
        />
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className="text-destructive text-sm">
            Failed to load job details. The job may not exist or you may not have permission to view it.
          </div>
        </div>
      </div>
    )
  }

  const getJobStatus = (job: any) => {
    if (job.final_status) {
      return job.final_status
    }

    if (job.executions && job.executions.length > 0) {
      const latestExecution = job.executions[0]
      return latestExecution.status
    }

    return 'pending'
  }

  // If we have a job, show the tracking interface
  if (jobUid && job) {
    const latestExecution = job.executions?.[0]
    const jobStatus = getJobStatus(job) as keyof typeof statusConfig
    const statusInfo = statusConfig[jobStatus] || statusConfig.manual

    return (
      <div className="space-y-6">
        <PageHeader
          icon={SECTION_ICONS.imports}
          title="Data Import Progress"
          description={`Tracking import job: ${job.uid}`}
          actions={
          <>
            {/* Dynamic Refresh Status */}
            {dynamicRefresh.isEnabled ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <AppIcon path={mdiAutorenew} size="sm" className="motion-safe:animate-spin" />
                <span>Auto-refresh: {(dynamicRefresh.refreshInterval / 1000).toFixed(1)}s</span>
                <span className="text-xs">•</span>
                <span>Next: {dynamicRefresh.timeRemaining}s</span>
                {dynamicRefresh.hasChanged !== null && (
                  <>
                    <span className="text-xs">•</span>
                    <span className={dynamicRefresh.hasChanged ? 'text-green-500' : 'text-yellow-500'}>
                      {dynamicRefresh.hasChanged ? 'Changes detected' : 'No changes'}
                    </span>
                  </>
                )}
              </div>
            ) : jobStatus === 'success' || jobStatus === 'failed' || jobStatus === 'cancelled' ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <AppIcon path={mdiCheckCircle} size="sm" />
                <span>Job completed</span>
              </div>
            ) : null}

            <div className="flex gap-2">
              {/* Manual Refresh Button */}
              <Button
                variant="outline"
                onClick={handleManualRefresh}
                disabled={isJobLoading}
                className="flex items-center gap-2"
                data-testid="manual-refresh-button"
              >
                <AppIcon path={mdiRefresh} size="sm" className={isJobLoading ? 'motion-safe:animate-spin' : ''} />
                {t('import.refresh')}
              </Button>
              {jobStatus === 'running' && (
                <Button
                  variant="destructive"
                  onClick={handleCancelJob}
                  disabled={cancelJobMutation.isPending}
                  className="flex items-center gap-2"
                >
                  <AppIcon path={mdiStop} size="md" />
                  {cancelJobMutation.isPending ? t('import.cancelling') : t('import.cancel_job')}
                </Button>
              )}
              {jobStatus === 'success' && !job.properties?.apply && (
                <Button
                  onClick={handleApplyImport}
                  disabled={isCreatingJob}
                  className="flex items-center gap-2"
                  data-testid="apply-import-button"
                >
                  <AppIcon path={mdiPlay} size="md" />
                  {isCreatingJob ? t('common:status.loading') : t('import.resume')}
                </Button>
              )}
              {(jobStatus === 'success' || jobStatus === 'failed' || jobStatus === 'cancelled') && (
                <Button
                  onClick={handleRunAgain}
                  disabled={isCreatingJob}
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="run-again-button"
                >
                  <AppIcon path={mdiReplay} size="md" />
                  {isCreatingJob ? t('common:status.loading') : t('import.restart')}
                </Button>
              )}
            </div>
          </>
          }
        />

        {/* Job Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AppIcon path={statusInfo.icon} size="md" />
              Import Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <Badge className={statusInfo.color}>
                {jobStatus.charAt(0).toUpperCase() + jobStatus.slice(1)}
              </Badge>
              {job.properties?.apply ? (
                <Badge variant="secondary">Apply Mode</Badge>
              ) : (
                <Badge variant="outline">Dry Run Mode</Badge>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Created</div>
                <div className="font-medium">{new Date(job.created_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Documents</div>
                <div className="font-medium font-mono text-xs">
                  {Array.isArray(job.properties?.document_uids)
                    ? `${(job.properties.document_uids as string[]).length} file(s)`
                    : (job.properties?.document_uid as string) || 'Unknown'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Cards */}
        {latestExecution?.output && (
          <div className="space-y-4">
            <ValidationResults output={latestExecution.output} />
            {latestExecution.output && typeof latestExecution.output === 'object' && 'import_summary' in latestExecution.output && (
              <ImportResults output={latestExecution.output} />
            )}
            <DataImportResults output={latestExecution.output} />
          </div>
        )}

        {/* Execution Logs */}
        {latestExecution && (
          <LatestExecutionLogs
            org={org}
            jobUid={job.uid}
            execution={latestExecution}
            limit={50}
          />
        )}
      </div>
    )
  }

  // Show upload interface
  return (
    <div className="space-y-6">
      <PageHeader
        icon={SECTION_ICONS.imports}
        title={t('import.title')}
        description={t('import.file_upload_hint')}
      />

      {/* Import Mode Information */}
      <Alert>
        <AppIcon path={mdiInformation} size="md" />
        <AlertDescription>
          <div className="space-y-3">
            <div className="space-y-2">
              <div>
                <strong>{t('import.info_dry_run_label')}</strong> {t('import.info_dry_run_desc')}
              </div>
              <div>
                <strong>{t('import.info_apply_label')}</strong> {t('import.info_apply_desc')}
              </div>
            </div>
            <div className="pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  {t('import.info_template_text')}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadSampleCSV}
                  className="flex items-center gap-2"
                  data-testid="download-sample-csv-button"
                >
                  <AppIcon path={mdiDownload} size="sm" />
                  {t('import.download_template')}
                </Button>
              </div>
            </div>
          </div>
        </AlertDescription>
      </Alert>

      {/* File Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AppIcon path={mdiCloudUpload} size="md" />
            {t('import.file_upload_title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!uploadFile ? (
            <div
              className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              data-testid="file-upload-zone"
            >
              <AppIcon tone="muted" path={mdiFileUpload} size="2xl" className="mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('import.file_drop_hint')}</h3>
              <p className="text-muted-foreground mb-4">{t('import.file_upload_hint')}</p>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>{t('import.file_size_hint')}</p>
                <p>{t('import.file_format_hint')}</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.json,.zip"
                onChange={handleFileInputChange}
                className="hidden"
                data-testid="file-input"
              />
            </div>
          ) : (
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <AppIcon path={mdiFileTable} size="md" />
                  <span className="font-medium">{uploadFile.file.name}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={clearFile} data-testid="clear-file-button">
                  {t('import.file_remove')}
                </Button>
              </div>
              <div className="text-sm text-muted-foreground">
                {t('import.file_size_label', { size: (uploadFile.file.size / 1024).toFixed(1) })}
              </div>
              {uploadFile.status === 'failed' && uploadFile.error && (
                <div className="text-sm text-destructive mt-2">
                  {t('import.file_error', { error: uploadFile.error })}
                </div>
              )}
            </div>
          )}

          {uploadError && (
            <Alert>
              <AppIcon path={mdiAlertCircle} size="md" />
              <AlertDescription>
                {uploadError}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Import Options */}
      <Card>
        <CardHeader>
          <CardTitle>{t('import.options_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="apply-mode"
              checked={importOptions.apply}
              onCheckedChange={(checked) =>
                setImportOptions(prev => ({ ...prev, apply: checked === true }))
              }
              data-testid="apply-mode-checkbox"
            />
            <Label htmlFor="apply-mode" className="text-sm font-medium">
              {t('import.apply_mode_label')}
            </Label>
          </div>
          <div className="text-sm text-muted-foreground ml-6">
            {importOptions.apply
              ? t('import.apply_mode_desc_apply')
              : t('import.apply_mode_desc_dry')
            }
          </div>

          <div className="border-t border-border pt-4 space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="global-mode"
                checked={importOptions.global}
                onCheckedChange={(checked) =>
                  setImportOptions(prev => ({ ...prev, global: checked === true }))
                }
                data-testid="global-mode-checkbox"
              />
              <Label htmlFor="global-mode" className="text-sm font-medium">
                {t('import.global_mode_label')}
              </Label>
            </div>
            <div className="text-sm text-muted-foreground ml-6">
              {t('import.global_mode_desc')}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="auto-create-sources"
                checked={importOptions.autoCreateSources}
                onCheckedChange={(checked) =>
                  setImportOptions(prev => ({ ...prev, autoCreateSources: checked === true }))
                }
                data-testid="auto-create-sources-checkbox"
              />
              <Label htmlFor="auto-create-sources" className="text-sm font-medium">
                {t('import.auto_create_sources_label')}
              </Label>
            </div>
            <div className="text-sm text-muted-foreground ml-6">
              {t('import.auto_create_sources_desc')}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="auto-create-asset-types"
                checked={importOptions.autoCreateAssetTypes}
                onCheckedChange={(checked) =>
                  setImportOptions(prev => ({ ...prev, autoCreateAssetTypes: checked === true }))
                }
                data-testid="auto-create-asset-types-checkbox"
              />
              <Label htmlFor="auto-create-asset-types" className="text-sm font-medium">
                {t('import.auto_create_asset_types_label')}
              </Label>
            </div>
            <div className="text-sm text-muted-foreground ml-6">
              {t('import.auto_create_asset_types_desc')}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="auto-create-properties"
                checked={importOptions.autoCreateProperties}
                onCheckedChange={(checked) =>
                  setImportOptions(prev => ({ ...prev, autoCreateProperties: checked === true }))
                }
                data-testid="auto-create-properties-checkbox"
              />
              <Label htmlFor="auto-create-properties" className="text-sm font-medium">
                {t('import.auto_create_properties_label')}
              </Label>
            </div>
            <div className="text-sm text-muted-foreground ml-6">
              {t('import.auto_create_properties_desc')}
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Start Import Button */}
      <div className="flex justify-center">
        <Button
          onClick={startImport}
          disabled={!uploadFile || isUploading}
          className="bg-primary hover:bg-primary/90 min-w-32"
          data-testid="start-import-button"
        >
          {isUploading ? (
            <>
              <AppIcon path={mdiLoading} size="md" className="mr-2 motion-safe:animate-spin" />
              {uploadFile?.progress && uploadFile.progress < 75 ? t('import.uploading') : t('common:status.loading')}
            </>
          ) : (
            <>
              <AppIcon path={mdiFileUpload} size="md" className="mr-2" />
              {importOptions.apply ? t('import.upload_button') : t('import.upload_button')}
            </>
          )}
        </Button>
      </div>

      {/* Recent Import Jobs */}
      <RecentImportJobs org={org} />
    </div>
  )
}
